import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  MapPin,
  Package,
  ClipboardCheck,
  Network,
  AlertTriangle,
  Users,
  Radio,
  Shield,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import {
  getAllContacts,
  getPendingMessages,
  getAllReunionPoints,
  getAllResources,
  getAllChecklistItems,
} from '../db/database';

interface Stats {
  contacts: number;
  pendingMessages: number;
  reunionPoints: number;
  resources: number;
  checklistProgress: number;
}

export function Dashboard() {
  const { isOnline } = useApp();
  const [stats, setStats] = useState<Stats>({
    contacts: 0,
    pendingMessages: 0,
    reunionPoints: 0,
    resources: 0,
    checklistProgress: 0,
  });
  const [, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const [contacts, messages, points, resources, checklist] = await Promise.all([
        getAllContacts(),
        getPendingMessages(),
        getAllReunionPoints(),
        getAllResources(),
        getAllChecklistItems(),
      ]);

      const checkedItems = checklist.filter(item => item.isChecked).length;
      const progress = checklist.length > 0 ? Math.round((checkedItems / checklist.length) * 100) : 0;

      setStats({
        contacts: contacts.length,
        pendingMessages: messages.length,
        reunionPoints: points.length,
        resources: resources.length,
        checklistProgress: progress,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }

  const quickActions = [
    {
      title: 'Emergency Contacts',
      description: `${stats.contacts} contacts saved`,
      icon: Phone,
      path: '/contacts',
      color: 'emergency',
    },
    {
      title: 'Message Queue',
      description: `${stats.pendingMessages} messages pending`,
      icon: MessageSquare,
      path: '/messages',
      color: stats.pendingMessages > 0 ? 'warning' : 'safe',
    },
    {
      title: 'Reunion Points',
      description: `${stats.reunionPoints} locations marked`,
      icon: MapPin,
      path: '/reunion',
      color: 'safe',
    },
    {
      title: 'Resources',
      description: `${stats.resources} items shared`,
      icon: Package,
      path: '/resources',
      color: 'warning',
    },
    {
      title: 'Emergency Checklist',
      description: `${stats.checklistProgress}% prepared`,
      icon: ClipboardCheck,
      path: '/checklist',
      color: stats.checklistProgress >= 80 ? 'safe' : stats.checklistProgress >= 50 ? 'warning' : 'emergency',
    },
    {
      title: 'Mesh Network',
      description: 'View network topology',
      icon: Network,
      path: '/mesh',
      color: 'safe',
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'emergency':
        return 'from-emergency-600 to-emergency-700 border-emergency-500';
      case 'warning':
        return 'from-warning-600 to-warning-700 border-warning-500';
      case 'safe':
        return 'from-safe-600 to-safe-700 border-safe-500';
      default:
        return 'from-gray-600 to-gray-700 border-gray-500';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl emergency-gradient p-6 lg:p-8"
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">
                Disaster Preparedness Center
              </h1>
              <p className="text-white/80">
                Stay connected, stay prepared, stay safe
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/80 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Family Safe</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.contacts}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/80 mb-1">
                <Radio className="w-4 h-4" />
                <span className="text-sm">Network Status</span>
              </div>
              <p className="text-2xl font-bold text-white">{isOnline ? 'Online' : 'Offline'}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/80 mb-1">
                <Activity className="w-4 h-4" />
                <span className="text-sm">Preparedness</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.checklistProgress}%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 text-white/80 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">Pending</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.pendingMessages}</p>
            </div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
      </motion.div>

      {/* Quick Actions Grid */}
      <div>
        <h2 className="section-title">
          <AlertTriangle className="w-5 h-5 text-emergency-400" />
          Quick Actions
        </h2>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {quickActions.map((action) => (
            <motion.div key={action.path} variants={itemVariants}>
              <Link
                to={action.path}
                className={`card group relative overflow-hidden p-5 flex items-center gap-4 border-l-4 ${
                  action.color === 'emergency'
                    ? 'border-l-emergency-500 hover:bg-emergency-900/20'
                    : action.color === 'warning'
                    ? 'border-l-warning-500 hover:bg-warning-900/20'
                    : 'border-l-safe-500 hover:bg-safe-900/20'
                } transition-all duration-300`}
              >
                <div
                  className={`p-3 rounded-xl bg-gradient-to-br ${getColorClasses(action.color)}`}
                >
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white group-hover:text-emergency-400 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-400">{action.description}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-emergency-400 group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Emergency Tips */}
      <div className="card p-6">
        <h2 className="section-title">
          <Shield className="w-5 h-5 text-safe-400" />
          Emergency Tips
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">Stay Informed</h3>
            <p className="text-sm text-gray-400">
              Keep your battery-powered radio handy and tune to local emergency broadcasts for updates.
            </p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">Know Your Routes</h3>
            <p className="text-sm text-gray-400">
              Plan multiple evacuation routes and identify reunion points for your family.
            </p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">72-Hour Kit</h3>
            <p className="text-sm text-gray-400">
              Maintain supplies for at least 72 hours including water, food, and first aid.
            </p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-2">Communication Plan</h3>
            <p className="text-sm text-gray-400">
              Have an out-of-area contact who can serve as a central point for family communications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
